import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';

const apiUrlSectores = `${process.env.REACT_APP_API_URL}/section`;
const apiUrlAddresses = `${process.env.REACT_APP_API_URL}/addres`;

const EditSection = () => {
    const { id_sector } = useParams();
    const navigate = useNavigate();
    const [sector, setNewSector] = useState("");
    const [sede, setSede] = useState("");
    const [sedes, setSedes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [originalSectorName, setOriginalSectorName] = useState("");

    useEffect(() => {
        loadData();
    }, [id_sector]);

    const loadData = async () => {
        try {
            const [sectorRes, sedesRes] = await Promise.all([
                axios.get(`${apiUrlSectores}${id_sector}`),
                axios.get(apiUrlAddresses)
            ]);

            const sectorData = sectorRes.data;
            setNewSector(sectorData.sector);
            setOriginalSectorName(sectorData.sector);
            if (sectorData.id_sede) setSede(String(sectorData.id_sede));
            setSedes(sedesRes.data);
        } catch (error) {
            console.error('Error al cargar datos:', error);
            alert('❌ Error al cargar el sector');
            navigate('/admin/AbmSectores');
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();

        if (!sector.trim()) {
            alert('⚠️ El nombre del sector es obligatorio');
            return;
        }

        if (!sede) {
            alert('⚠️ Debe seleccionar una sede');
            return;
        }

        setIsSubmitting(true);
        try {
            const updatesectorData = {
                sector: sector.trim(),
                id_sede: sede ? Number(sede) : null
            };

            await axios.put(`${apiUrlSectores}${id_sector}`, updatesectorData);
            alert('✅ Sector actualizado exitosamente');
            navigate('/admin/AbmSectores');
        } catch (error) {
            console.error('Error al actualizar el sector:', error);
            alert('❌ Error al actualizar: ' + (error.response?.data?.error || error.message));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate('/admin/AbmSectores');
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-100 flex items-center justify-center">
                <div className="bg-white p-8 rounded-2xl shadow-xl">
                    <div className="flex items-center gap-4">
                        <svg className="animate-spin h-8 w-8 text-green-600" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span className="text-lg font-semibold text-gray-700">Cargando datos del sector...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-100 py-8 px-4">
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-t-2xl shadow-xl p-6 border-b-4 border-emerald-500">
                    <div className="flex items-center gap-4">
                        <div className="bg-gradient-to-br from-emerald-500 to-green-600 p-4 rounded-xl shadow-lg">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Editar Sector</h1>
                            <p className="text-gray-600 mt-1">Modifica los datos de <span className="font-semibold text-emerald-600">{originalSectorName}</span></p>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleUpdate} className="bg-white rounded-b-2xl shadow-xl p-8">
                    <div className="space-y-6">
                        {/* Sede */}
                        <div>
                            <label htmlFor="sede" className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                <span className="text-blue-600">📍</span>
                                Sede
                                <span className="text-red-500">*</span>
                            </label>
                            <select
                                id="sede"
                                value={sede}
                                onChange={(e) => setSede(e.target.value)}
                                required
                                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none text-gray-800"
                            >
                                <option value="">Seleccione una sede...</option>
                                {sedes.map(s => (
                                    <option key={s.id_direccion} value={s.id_direccion}>
                                        {s.nombre_sede || `${s.direccion} - ${s.localidad}`}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Nombre del Sector */}
                        <div>
                            <label htmlFor="sector" className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                <span className="text-green-600">🏢</span>
                                Nombre del Sector
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="sector"
                                placeholder="Ej: Recursos Humanos, Finanzas, Producción"
                                value={sector}
                                onChange={(e) => setNewSector(e.target.value)}
                                required
                                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all outline-none text-gray-800 placeholder-gray-400"
                            />
                        </div>
                    </div>

                    {/* Botones */}
                    <div className="flex flex-col sm:flex-row gap-4 mt-8 pt-6 border-t-2 border-gray-200">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-1 bg-gradient-to-r from-emerald-600 to-green-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:from-emerald-700 hover:to-green-700 transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                        >
                            {isSubmitting ? (
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Actualizando...
                                </span>
                            ) : (
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Guardar Cambios
                                </span>
                            )}
                        </button>

                        <button
                            type="button"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-3 px-6 rounded-lg shadow-md transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="flex items-center justify-center gap-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Cancelar
                            </span>
                        </button>
                    </div>
                </form>

                {/* Info adicional */}
                <div className="mt-6 bg-emerald-50 border-l-4 border-emerald-500 p-4 rounded-r-lg shadow-md">
                    <div className="flex items-start gap-3">
                        <svg className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div>
                            <p className="text-sm font-semibold text-emerald-900">Ten en cuenta</p>
                            <p className="text-sm text-emerald-700 mt-1">
                                Cambiar la sede del sector puede afectar a los usuarios asignados. Los pedidos históricos mantendrán su referencia original.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditSection;
